import { useEffect, useState } from 'react';
import {
  makeStyles,
  tokens,
  Field,
  Input,
  Dropdown,
  Option,
  Combobox,
  Persona,
  Spinner,
} from '@fluentui/react-components';
import { DatePicker } from '@fluentui/react-datepicker-compat';
import { OnboardingFormData } from '../../../hooks/useOnboardingForm';
import { apiClient, ListResponse } from '../../../api/client';
import { EMPLOYMENT_TYPES } from '../../../config/constants';
import { useAuth } from '../../../hooks/useAuth';

const useStyles = makeStyles({
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: tokens.spacingHorizontalL,
  },
  fullWidth: {
    gridColumn: '1 / -1',
  },
  section: {
    marginBottom: tokens.spacingVerticalXL,
  },
  sectionTitle: {
    fontSize: tokens.fontSizeBase400,
    fontWeight: tokens.fontWeightSemibold,
    marginBottom: tokens.spacingVerticalM,
    color: tokens.colorNeutralForeground1,
  },
});

interface UserDetailsStepProps {
  formData: OnboardingFormData;
  updateFormData: <K extends keyof OnboardingFormData>(field: K, value: OnboardingFormData[K]) => void;
  errors: Record<string, string>;
}

interface Site {
  _id: string;
  SITE_CODE: string;
  Company_Name: string;
  City: string;
  Country: string;
}

interface GraphUser {
  id: string;
  displayName: string;
  mail: string;
  jobTitle?: string;
}

export function UserDetailsStep({ formData, updateFormData, errors }: UserDetailsStepProps) {
  const styles = useStyles();
  const { isLoading: authLoading } = useAuth();
  const [sites, setSites] = useState<Site[]>([]);
  const [sitesLoading, setSitesLoading] = useState(true);
  const [managerQuery, setManagerQuery] = useState('');
  const [managers, setManagers] = useState<GraphUser[]>([]);
  const [managersLoading, setManagersLoading] = useState(false);

  // Load sites only after auth is ready
  useEffect(() => {
    if (authLoading) return;

    apiClient
      .get<ListResponse<Site>>('/sites')
      .then((response) => setSites(response.data))
      .catch(console.error)
      .finally(() => setSitesLoading(false));
  }, [authLoading]);

  // Search managers
  useEffect(() => {
    if (managerQuery.length < 2) {
      setManagers([]);
      return;
    }

    const timer = setTimeout(async () => {
      setManagersLoading(true);
      try {
        const response = await apiClient.get<ListResponse<GraphUser>>(
          `/users/search?q=${encodeURIComponent(managerQuery)}`
        );
        setManagers(response.data);
      } catch (error) {
        console.error('Error searching managers:', error);
      } finally {
        setManagersLoading(false);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [managerQuery]);

  const handleManagerSelect = (user: GraphUser) => {
    updateFormData('lineManagerId', user.id);
    updateFormData('lineManagerName', user.displayName);
    updateFormData('lineManagerEmail', user.mail);
    setManagerQuery(user.displayName);
  };

  return (
    <div>
      <div className={styles.section}>
        <div className={styles.sectionTitle}>Personal Information</div>
        <div className={styles.grid}>
          <Field
            label="First Name"
            required
            validationMessage={errors.firstName}
            validationState={errors.firstName ? 'error' : undefined}
          >
            <Input
              value={formData.firstName}
              onChange={(_, data) => updateFormData('firstName', data.value)}
              placeholder="Enter first name"
            />
          </Field>

          <Field
            label="Last Name"
            required
            validationMessage={errors.lastName}
            validationState={errors.lastName ? 'error' : undefined}
          >
            <Input
              value={formData.lastName}
              onChange={(_, data) => updateFormData('lastName', data.value)}
              placeholder="Enter last name"
            />
          </Field>

          <Field label="Display Name" className={styles.fullWidth}>
            <Input
              value={formData.displayName}
              onChange={(_, data) => updateFormData('displayName', data.value)}
              placeholder="Auto-generated from first and last name"
            />
          </Field>

          <Field
            label="Job Title"
            required
            validationMessage={errors.jobTitle}
            validationState={errors.jobTitle ? 'error' : undefined}
          >
            <Input
              value={formData.jobTitle}
              onChange={(_, data) => updateFormData('jobTitle', data.value)}
              placeholder="Enter job title"
            />
          </Field>

          <Field
            label="Department"
            required
            validationMessage={errors.department}
            validationState={errors.department ? 'error' : undefined}
          >
            <Input
              value={formData.department}
              onChange={(_, data) => updateFormData('department', data.value)}
              placeholder="Enter department"
            />
          </Field>

          <Field label="Role" required>
            <Dropdown
              value={formData.role}
              onOptionSelect={(_, data) =>
                updateFormData('role', data.optionValue as 'Employee' | 'Contractor')
              }
            >
              {EMPLOYMENT_TYPES.map((type) => (
                <Option key={type.key} value={type.key} text={type.text}>
                  {type.text}
                </Option>
              ))}
            </Dropdown>
          </Field>

          <Field label="Mobile Phone">
            <Input
              value={formData.mobilePhone}
              onChange={(_, data) => updateFormData('mobilePhone', data.value)}
              placeholder="+1-555-123-4567"
            />
          </Field>

          <Field label="Business Phone">
            <Input
              value={formData.businessPhone}
              onChange={(_, data) => updateFormData('businessPhone', data.value)}
              placeholder="+1-555-123-4567 x123"
            />
          </Field>
        </div>
      </div>

      <div className={styles.section}>
        <div className={styles.sectionTitle}>Work Location & Manager</div>
        <div className={styles.grid}>
          <Field
            label="Site Code"
            required
            validationMessage={errors.siteCode}
            validationState={errors.siteCode ? 'error' : undefined}
          >
            <Dropdown
              value={formData.siteCode}
              placeholder={sitesLoading ? 'Loading sites...' : 'Select a site'}
              onOptionSelect={(_, data) => updateFormData('siteCode', data.optionValue as string)}
            >
              {sites.map((site) => (
                <Option key={site._id} value={site.SITE_CODE} text={`${site.SITE_CODE} - ${site.Company_Name}`}>
                  {site.SITE_CODE} - {site.Company_Name} ({site.City}, {site.Country})
                </Option>
              ))}
            </Dropdown>
          </Field>

          <Field
            label="Line Manager"
            required
            validationMessage={errors.lineManager}
            validationState={errors.lineManager ? 'error' : undefined}
          >
            <Combobox
              value={managerQuery || formData.lineManagerName}
              onChange={(e) => setManagerQuery((e.target as HTMLInputElement).value)}
              placeholder="Start typing to search..."
              freeform
            >
              {managersLoading && (
                <Option value="" text="Searching..." disabled>
                  <Spinner size="tiny" /> Searching...
                </Option>
              )}
              {managers.map((user) => (
                <Option
                  key={user.id}
                  value={user.id}
                  text={user.displayName}
                  onClick={() => handleManagerSelect(user)}
                >
                  <Persona
                    name={user.displayName}
                    secondaryText={user.mail}
                    tertiaryText={user.jobTitle}
                    size="small"
                  />
                </Option>
              ))}
            </Combobox>
          </Field>

          <Field
            label="Start Date"
            required
            validationMessage={errors.startDate}
            validationState={errors.startDate ? 'error' : undefined}
          >
            <DatePicker
              value={formData.startDate}
              onSelectDate={(date) => updateFormData('startDate', date ?? null)}
              placeholder="Select start date"
            />
          </Field>

          <Field
            label="End Date"
            hint={formData.role === 'Contractor' ? 'Required for contractors' : 'Leave empty for permanent employees'}
          >
            <DatePicker
              value={formData.endDate}
              onSelectDate={(date) => updateFormData('endDate', date ?? null)}
              placeholder="Select end date (if applicable)"
            />
          </Field>
        </div>
      </div>
    </div>
  );
}
